import React from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe, faServer, faPlug, faCloud,
  faCodeBranch, faPuzzlePiece, faChalkboardTeacher,
  faMagnifyingGlass, faLightbulb, faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
//import Footer from '../Footer';
import './Services.css';

/* ─── Animation variants ─────────────────────────────────────── */
var fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};

var stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.08 } },
};

/* ─── Services data ──────────────────────────────────────────── */
var SERVICES = [
  {
    icon:  faGlobe,
    title: 'Web & App Development',
    desc:  'Responsive, user-friendly applications built with React, React Native, Flutter, HTML, CSS, and JavaScript — crafted for performance on every device.',
    tag:   'Frontend',
  },
  {
    icon:  faServer,
    title: 'Backend Development',
    desc:  'Robust, scalable server-side systems using Node.js, PHP (Laravel), and Python, backed by MongoDB and SQL for reliable data management.',
    tag:   'Backend',
  },
  {
    icon:  faPlug,
    title: 'API Development & Integration',
    desc:  'Designing and integrating RESTful APIs to seamlessly connect front-end interfaces with back-end services and third-party platforms.',
    tag:   'Integration',
  },
  {
    icon:  faCloud,
    title: 'Cloud Deployment',
    desc:  'Deploying applications on AWS, Heroku, or Netlify — configured for security, scalability, and high availability in production environments.',
    tag:   'DevOps',
  },
  {
    icon:  faCodeBranch,
    title: 'Version Control & CI/CD',
    desc:  'Managing codebases and team collaboration with Git, GitHub, and automated CI/CD pipelines for smooth, consistent delivery.',
    tag:   'Tooling',
  },
  {
    icon:  faPuzzlePiece,
    title: 'Custom Software Solutions',
    desc:  'Tailored software built around your specific business requirements — high-performance, maintainable, and built to last.',
    tag:   'Consulting',
  },
  {
    icon:  faChalkboardTeacher,
    title: 'Teaching & Mentorship',
    desc:  'Guiding individuals and teams through coding, software development principles, and modern best practices — from beginner to advanced levels.',
    tag:   'Education',
  },
  {
    icon:  faMagnifyingGlass,
    title: 'Research & Exploration',
    desc:  'Continuously exploring emerging technologies, methodologies, and industry trends to deliver cutting-edge, future-proof solutions.',
    tag:   'Innovation',
  },
  {
    icon:  faLightbulb,
    title: 'Problem-Solving & Strategy',
    desc:  'Analysing complex technical challenges and architecting effective, innovative solutions that meet diverse business and user needs.',
    tag:   'Strategy',
  },
];

/* ─── Component ──────────────────────────────────────────────── */
var Services = function() {
  return (
    <motion.div
      className="services-root"
      initial="hidden"
      animate="show"
      variants={stagger}
    >

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="services-hero" aria-label="Services header">
        <div className="services-hero__glow services-hero__glow--1" aria-hidden="true" />
        <div className="services-hero__glow services-hero__glow--2" aria-hidden="true" />
        <Container>
          <motion.span className="home-label" variants={fadeUp}>What I Offer</motion.span>
          <motion.h1 className="services-hero__title" variants={fadeUp}>
            Services built for<br /><em>real results.</em>
          </motion.h1>
          <motion.p className="services-hero__sub" variants={fadeUp}>
            From a single landing page to a fully integrated cloud-deployed system —
            I bring the full stack to every engagement.
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link to="/contact" className="home-btn home-btn--primary">
              Start a Project <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </motion.div>
        </Container>
      </section>

      {/* ── Services grid ────────────────────────────────────── */}
      <section className="services-section" aria-labelledby="services-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Services</span>
            <h2 id="services-heading" className="home-section__title">All Services</h2>
            <p className="home-section__sub">
              A comprehensive suite of development, deployment, and consulting services.
            </p>
          </motion.div>

          <motion.div className="services-grid" variants={stagger}>
            {SERVICES.map(function(svc, i) {
              return (
                <motion.article
                  key={i}
                  className="svc-card"
                  variants={fadeUp}
                  whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  aria-label={svc.title}
                >
                  <div className="svc-card__top">
                    <div className="svc-card__icon" aria-hidden="true">
                      <FontAwesomeIcon icon={svc.icon} />
                    </div>
                    <span className="svc-card__tag">{svc.tag}</span>
                  </div>
                  <h3 className="svc-card__title">{svc.title}</h3>
                  <p className="svc-card__desc">{svc.desc}</p>
                </motion.article>
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* ── CTA banner ───────────────────────────────────────── */}
      <section className="services-cta" aria-label="Call to action">
        <Container>
          <motion.div className="services-cta__inner" variants={fadeUp}>
            <div>
              <h2 className="services-cta__title">Ready to work together?</h2>
              <p className="services-cta__sub">
                Let's discuss your project and build something exceptional.
              </p>
            </div>
            <div className="services-cta__actions">
              <Link to="/contact" className="home-btn home-btn--primary">
                Hire Me <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/projects" className="home-btn home-btn--outline">
                See My Work
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>
    </motion.div>
  );
};

export default Services;